import { formatMoney } from "@bw/shared/utils";
import type { CartSlot } from "../state/cartStore";
import CloseRounded from "@mui/icons-material/CloseRounded";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

/**
 * A compact court chip for the cart line. Prefers the trailing number
 * ("Court 1" → "Court · 1") so the cart reads as a schedule led by time without
 * repeating the full grid-header name; falls back to the full name otherwise.
 */
function courtNumber(courtName: string): string {
  const m = /(\d+)\s*$/.exec(courtName);
  return m ? `Court · ${m[1]}` : courtName;
}

/** Format an ISO start..end into a venue-local "9:00–10:00 AM" range. */
function timeRange(start: string, end: string, timezone: string): string {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      hour: "numeric",
      minute: "2-digit",
    });
    return `${fmt.format(new Date(start))}–${fmt.format(new Date(end))}`;
  } catch {
    return "";
  }
}

/**
 * One cart line: court · time · $estimate · remove. The remove control is a
 * ≥44px target. The price is the per-slot ESTIMATE (or "—" when unpriced).
 */
export function CartItemRow({
  item,
  timezone,
  onRemove,
}: {
  item: CartSlot;
  timezone: string;
  onRemove: () => void;
}) {
  const range = timeRange(item.start_time, item.end_time, timezone);
  return (
    <Stack
      direction="row"
      spacing={1.5}
      aria-label={`${item.court_name}, ${range}, 1 hour`}
      sx={{ alignItems: "center", py: 1.25 }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* The court is announced via the row's aria-label; the visible primary
            text leads with the time so the cart reads as a day's schedule. The
            court NUMBER (not the full "Court N") shows as a compact chip. */}
        <Typography sx={{ fontWeight: 700 }}>{range}</Typography>
        <Stack
          direction="row"
          spacing={1}
          sx={{ mt: 0.5, alignItems: "center" }}
        >
          <Chip
            label={courtNumber(item.court_name)}
            size="small"
            aria-hidden="true"
          />
          <Typography variant="caption" color="text.secondary">
            1 hr
          </Typography>
        </Stack>
      </Box>
      <Typography sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
        {item.estimate === null
          ? "—"
          : formatMoney(item.estimate, item.currency)}
      </Typography>
      <IconButton
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${item.court_name}, ${range} from cart`}
      >
        <CloseRounded />
      </IconButton>
    </Stack>
  );
}
